import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth, signInWithPopup, GoogleAuthProvider, onAuthStateChanged, User, signOut } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import firebaseConfig from '../../firebase-applet-config.json';

// Initialize Firebase App safely if not already initialized
const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();
export const auth = getAuth(app);
export const db = getFirestore(app, (firebaseConfig as any).firestoreDatabaseId);

// Configure Google Auth Provider with Workspace scopes
export const googleWorkspaceProvider = new GoogleAuthProvider();
googleWorkspaceProvider.addScope('https://www.googleapis.com/auth/calendar');
googleWorkspaceProvider.addScope('https://www.googleapis.com/auth/drive.file');
googleWorkspaceProvider.addScope('https://www.googleapis.com/auth/spreadsheets');
googleWorkspaceProvider.addScope('https://www.googleapis.com/auth/meetings.space.created');
googleWorkspaceProvider.addScope('https://www.googleapis.com/auth/gmail.send');
googleWorkspaceProvider.addScope('https://www.googleapis.com/auth/gmail.readonly');

// EXPERT COMMENTARY: Local persistence layer for Google Workspace access token.
// Storing the token securely in localStorage enables the Google Drive and Google Calendar integrations
// to remain "always connected", persisting seamlessly even through page refreshes or route changes.
let cachedAccessToken: string | null = (() => {
  try {
    return localStorage.getItem("clinic_workspace_access_token");
  } catch (e) {
    console.error("Clinical System Storage Warning: Failed to retrieve cached Workspace token:", e);
    return null;
  }
})();
let isSigningIn = false;

/**
 * Handle onAuthStateChanged subscription with token retrieval
 */
export const initWorkspaceAuth = (
  onAuthSuccess: (user: User, token: string) => void,
  onAuthFailure: () => void
) => {
  return onAuthStateChanged(auth, async (user) => {
    if (user) {
      if (cachedAccessToken) {
        onAuthSuccess(user, cachedAccessToken);
      } else if (!isSigningIn) {
        // Sign-out state fallback
        onAuthFailure();
      }
    } else {
      cachedAccessToken = null;
      try {
        localStorage.removeItem("clinic_workspace_access_token");
      } catch (e) {}
      onAuthFailure();
    }
  });
};

/**
 * Perform manual Google Auth Popup Sign-in
 */
export const signInWithGoogleWorkspace = async (): Promise<{ user: User; accessToken: string } | null> => {
  try {
    isSigningIn = true;
    const result = await signInWithPopup(auth, googleWorkspaceProvider);
    const credential = GoogleAuthProvider.credentialFromResult(result);
    if (!credential?.accessToken) {
      throw new Error('لم يتم استرجاع رمز الوصول (Access Token) الخاص بـ Google Workspace.');
    }
    cachedAccessToken = credential.accessToken;
    try {
      localStorage.setItem("clinic_workspace_access_token", cachedAccessToken);
    } catch (e) {
      console.error("Clinical System Storage Warning: Failed to save Workspace token:", e);
    }
    return { user: result.user, accessToken: cachedAccessToken };
  } catch (error: any) {
    console.error('[Workspace OAuth] Google Sign-In failed:', error);
    throw error;
  } finally {
    isSigningIn = false;
  }
};

/**
 * Retrieve current active access token from memory
 */
export const getWorkspaceAccessToken = (): string | null => {
  if (!cachedAccessToken) {
    try {
      cachedAccessToken = localStorage.getItem("clinic_workspace_access_token");
    } catch (e) {}
  }
  return cachedAccessToken;
};

/**
 * Disconnect Google Workspace session
 */
export const logoutWorkspace = async () => {
  await signOut(auth);
  cachedAccessToken = null;
  try {
    localStorage.removeItem("clinic_workspace_access_token");
  } catch (e) {}
};

/**
 * Safe Google Calendar API event creation on the primary calendar
 * Includes detailed clinical metadata, timing constraints, and full error tracing.
 */
export const createGoogleCalendarEvent = async (
  accessToken: string,
  appointment: {
    id: string;
    patientName: string;
    department: string;
    physicianName: string;
    dateTime: string;
  }
): Promise<{ success: boolean; eventUrl?: string; error?: string }> => {
  try {
    const startTime = new Date(appointment.dateTime);
    // Clinical default duration set to 30 minutes for patient appointments
    const endTime = new Date(startTime.getTime() + 30 * 60 * 1000);

    const eventPayload = {
      summary: `عيادة IQ-doctor: ${appointment.patientName} (${appointment.department})`,
      description: `استشارة طبية مجدولة رقم #${appointment.id}\nالتخصص: ${appointment.department}\nالطبيب المعالج: ${appointment.physicianName}\n\n* تم توليد هذا الموعد وتزامن المزامنة تلقائياً بواسطة نظام IQ-Doctor Clinic الموثق.`,
      start: {
        dateTime: startTime.toISOString(),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      },
      end: {
        dateTime: endTime.toISOString(),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      },
      reminders: {
        useDefault: true,
      },
    };

    const response = await fetch('https://www.googleapis.com/calendar/v3/calendars/primary/events', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(eventPayload),
    });

    const data = await response.json();

    if (!response.ok) {
      if (response.status === 401) {
        cachedAccessToken = null;
        try { localStorage.removeItem("clinic_workspace_access_token"); } catch(e) {}
        throw new Error("Your Google session has expired. Please sign out and reconnect your Google Calendar.");
      }
      throw new Error(data.error?.message || 'شاب عملية إنشاء موعد Google Calendar خطأ.');
    }

    return {
      success: true,
      eventUrl: data.htmlLink,
    };
  } catch (err: any) {
    console.error('[Google Calendar API Error]:', err);
    return {
      success: false,
      error: err.message || 'خطأ اتصال غير معروف.',
    };
  }
};

export const uploadFileToDrive = async (
  accessToken: string,
  fileName: string,
  content: string
): Promise<{ success: boolean; fileUrl?: string; error?: string }> => {
  try {
    const FOLDER_NAME = 'IQ-Doctor Backups';
    let folderId = null;

    // Search for existing folder
    const searchRes = await fetch(`https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(`name='${FOLDER_NAME}' and mimeType='application/vnd.google-apps.folder' and trashed=false`)}&fields=files(id)`, {
      headers: { Authorization: `Bearer ${accessToken}` }
    });
    const searchData = await searchRes.json();

    if (searchData.files && searchData.files.length > 0) {
      folderId = searchData.files[0].id;
    } else {
      // Create folder
      const folderMetadata = {
        name: FOLDER_NAME,
        mimeType: 'application/vnd.google-apps.folder'
      };
      const createRes = await fetch('https://www.googleapis.com/drive/v3/files', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(folderMetadata)
      });
      const createData = await createRes.json();
      if (createData.id) folderId = createData.id;
    }

    const metadata: any = {
      name: fileName,
      mimeType: 'text/plain',
    };
    if (folderId) {
      metadata.parents = [folderId];
    }

    const form = new FormData();
    form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
    form.append('file', new Blob([content], { type: 'text/plain' }));

    const response = await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: form,
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error?.message || 'خطأ في عملية رفع الملف إلى Google Drive.');
    }

    return {
      success: true,
      fileUrl: `https://drive.google.com/open?id=${data.id}`,
    };
  } catch (err: any) {
    console.error('[Google Drive API Error]:', err);
    return {
      success: false,
      error: err.message || 'خطأ اتصال غير معروف.',
    };
  }
};

/**
 * Lists clinical JSON backups created by this app from Google Drive.
 * Specifically searches for files with the prefix 'clinic-data-backup-' and
 * sorts them from newest to oldest.
 */
export const listBackupFilesFromDrive = async (
  accessToken: string
): Promise<{ success: boolean; files?: { id: string; name: string; createdTime?: string }[]; error?: string }> => {
  try {
    const q = encodeURIComponent("name contains 'clinic-data-backup-' and mimeType != 'application/vnd.google-apps.folder' and trashed = false");
    const response = await fetch(`https://www.googleapis.com/drive/v3/files?q=${q}&orderBy=createdTime%20desc&fields=files(id,name,createdTime)`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error?.message || 'خطأ في جلب قائمة الملفات من Google Drive.');
    }

    return {
      success: true,
      files: data.files || [],
    };
  } catch (err: any) {
    console.error('[Google Drive List Error]:', err);
    return {
      success: false,
      error: err.message || 'خطأ في الاتصال أثناء جلب الملفات.',
    };
  }
};

/**
 * Downloads a specific clinic JSON backup file content by ID from Google Drive.
 */
export const downloadFileFromDrive = async (
  accessToken: string,
  fileId: string
): Promise<{ success: boolean; content?: string; error?: string }> => {
  try {
    const response = await fetch(`https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error?.message || 'خطأ في تحميل الملف من Google Drive.');
    }

    const content = await response.text();
    return {
      success: true,
      content,
    };
  } catch (err: any) {
    console.error('[Google Drive Download Error]:', err);
    return {
      success: false,
      error: err.message || 'خطأ في الاتصال أثناء تحميل الملف.',
    };
  }
};

export const appendDataToSheet = async (
  accessToken: string,
  spreadsheetId: string,
  range: string,
  values: any[][]
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await fetch(
      `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${range}:append?valueInputOption=USER_ENTERED`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ values }),
      }
    );

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error?.message || 'خطأ في إضافة البيانات إلى Google Sheet.');
    }

    return { success: true };
  } catch (err: any) {
    console.error('[Google Sheets API Error]:', err);
    return { success: false, error: err.message || 'خطأ اتصال غير معروف.' };
  }
};

export const createGoogleSheet = async (accessToken: string, title: string): Promise<{ success: boolean; spreadsheetId?: string; sheetUrl?: string; error?: string }> => {
  try {
    const response = await fetch('https://sheets.googleapis.com/v4/spreadsheets', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        properties: {
          title: title,
        },
      }),
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error?.message || 'Failed to create Google Sheet.');
    }

    return {
      success: true,
      spreadsheetId: data.spreadsheetId,
      sheetUrl: data.spreadsheetUrl,
    };
  } catch (err: any) {
    console.error('[Google Sheets API Error]:', err);
    return { success: false, error: err.message || 'Unknown network error.' };
  }
};

export const createGoogleMeetSpace = async (accessToken: string): Promise<{ success: boolean; meetUrl?: string; error?: string }> => {
  try {
    const response = await fetch('https://meet.googleapis.com/v2/spaces', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({}),
    });
    
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error?.message || 'Failed to create Google Meet space.');
    }
    
    return {
      success: true,
      meetUrl: data.meetingUri
    };
  } catch (err: any) {
    console.error('[Google Meet API Error]:', err);
    return { success: false, error: err.message || 'Unknown network error.' };
  }
};

/**
 * EXPERT COMMENTARY: Utility to encode string to base64url format required by Gmail API raw message payload.
 * Correctly supports UTF-8 and Unicode strings (Arabic/English names & medical notations).
 */
const base64UrlEncode = (str: string): string => {
  const utf8Bytes = new TextEncoder().encode(str);
  let binary = "";
  for (let i = 0; i < utf8Bytes.length; i++) {
    binary += String.fromCharCode(utf8Bytes[i]);
  }
  const base64 = btoa(binary);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

/**
 * MANDATORY INCLUSION: Send secure, formatted emails via the official Gmail API send endpoint.
 * Includes complete clinical metadata formatting, HTML mail support, and secure try/catch flow.
 */
export const sendGmailEmail = async (
  accessToken: string,
  to: string,
  subject: string,
  htmlContent: string
): Promise<{ success: boolean; messageId?: string; error?: string }> => {
  try {
    const cleanTo = to.trim();
    if (!cleanTo || !cleanTo.includes('@')) {
      throw new Error('الرقم البريدي أو عنوان البريد الإلكتروني غير صالح.');
    }

    // Build formal MIME email body
    const rawMessage = [
      `To: ${cleanTo}`,
      'Content-Type: text/html; charset=utf-8',
      'MIME-Version: 1.0',
      `Subject: =?utf-8?B?${btoa(unescape(encodeURIComponent(subject)))}?=`,
      '',
      htmlContent
    ].join('\r\n');

    const encodedRaw = base64UrlEncode(rawMessage);

    const response = await fetch('https://gmail.googleapis.com/gmail/v1/users/me/messages/send', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        raw: encodedRaw
      })
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error?.message || 'فشلت عملية إرسال البريد الإلكتروني عبر Gmail.');
    }

    return {
      success: true,
      messageId: data.id
    };
  } catch (err: any) {
    console.error('[Gmail API Error]:', err);
    return {
      success: false,
      error: err.message || 'خطأ اتصال غير معروف في Gmail.'
    };
  }
};

/**
 * MANDATORY INCLUSION: Send secure, formatted emails with attachments via the official Gmail API send endpoint.
 * Includes multipart MIME message creation, UTF-8 Subject and body support, and secure base64 PDF attachments.
 */
export const sendGmailEmailWithAttachment = async (
  accessToken: string,
  to: string,
  subject: string,
  htmlContent: string,
  attachment: {
    filename: string;
    contentType: string;
    base64Data: string;
  }
): Promise<{ success: boolean; messageId?: string; error?: string }> => {
  try {
    const cleanTo = to.trim();
    if (!cleanTo || !cleanTo.includes('@')) {
      throw new Error('الرقم البريدي أو عنوان البريد الإلكتروني غير صالح.');
    }

    const boundary = "------=_NextPart_" + Math.random().toString(36).substring(2);

    // Encode subject using UTF-8 Base64 to bypass character issues
    const encodedSubject = btoa(unescape(encodeURIComponent(subject)));

    // Construct a beautiful multipart mixed RFC822 compliant message structure
    const rawMessage = [
      `To: ${cleanTo}`,
      `Subject: =?utf-8?B?${encodedSubject}?=`,
      'MIME-Version: 1.0',
      `Content-Type: multipart/mixed; boundary="${boundary}"`,
      '',
      `--${boundary}`,
      'Content-Type: text/html; charset=utf-8',
      'Content-Transfer-Encoding: base64',
      '',
      btoa(unescape(encodeURIComponent(htmlContent))),
      '',
      `--${boundary}`,
      `Content-Type: ${attachment.contentType}; name="${attachment.filename}"`,
      'Content-Transfer-Encoding: base64',
      `Content-Disposition: attachment; filename="${attachment.filename}"`,
      '',
      attachment.base64Data,
      '',
      `--${boundary}--`
    ].join('\r\n');

    const encodedRaw = base64UrlEncode(rawMessage);

    const response = await fetch('https://gmail.googleapis.com/gmail/v1/users/me/messages/send', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        raw: encodedRaw
      })
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error?.message || 'فشلت عملية إرسال البريد الإلكتروني عبر Gmail.');
    }

    return {
      success: true,
      messageId: data.id
    };
  } catch (err: any) {
    console.error('[Gmail API Error with Attachment]:', err);
    return {
      success: false,
      error: err.message || 'خطأ اتصال غير معروف في Gmail.'
    };
  }
};

